import type { FetcherOptions } from "../types/api";
import type { PublicClientApplication } from "@azure/msal-browser";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/** Thrown by {@link fetcher} (via {@link createFetcherError}) for HTTP/network failures. */
export type FetcherError = Error & {
  status?: number;
  body?: string;
  isNetworkError?: boolean;
};

export function createFetcherError(
  message: string,
  options?: { status?: number; body?: string; isNetworkError?: boolean },
): FetcherError {
  const e = new Error(message) as FetcherError;
  e.name = "FetcherError";
  e.status = options?.status;
  e.body = options?.body;
  e.isNetworkError = options?.isNetworkError;
  return e;
}

export function isFetcherError(e: unknown): e is FetcherError {
  return e instanceof Error && e.name === "FetcherError";
}

/** Statuses that mean the session is invalid — app should clear auth and show login. */
export function isAuthDeniedStatus(status: number | undefined): boolean {
  return status === 401 || status === 403;
}

// Global MSAL instance, init promise and callback
let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<void> | null = null;
let onAuthError: (() => void | Promise<void>) | null = null;

/**
 * Initialize the fetcher with MSAL instance
 * Call this once at app startup
 */
export const initializeFetcher = (
  instance: PublicClientApplication,
  initPromise: Promise<void>,
  authErrorCallback?: () => void | Promise<void>,
) => {
  msalInstance = instance;
  msalInitPromise = initPromise;
  onAuthError = authErrorCallback || null;
};

/**
 * Clear MSAL token cache (no redirect).
 * Use when session is invalid or the user must sign in again.
 */
export async function clearAuthState(): Promise<void> {
  if (!msalInstance) return;
  try {
    if (msalInitPromise) {
      await msalInitPromise;
    }
    await msalInstance.clearCache();
  } catch (e) {
    console.error("clearAuthState failed:", e);
  }
}

/**
 * Get a valid token from MSAL, automatically refreshing if needed.
 * Exported so publicFetcher and other services can reuse it.
 */
export const getValidToken = async (): Promise<string | null> => {
  if (!msalInstance) {
    console.warn("MSAL instance not initialized");
    return null;
  }

  if (msalInitPromise) {
    try {
      await msalInitPromise;
    } catch (error) {
      console.error("MSAL initialization failed:", error);
      return null;
    }
  }

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  const account = accounts[0];

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["openid"],
      account: account,
    });
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Don't auto-popup — this can fire on page refresh before MSAL
      // cache is fully hydrated. Let onAuthError handle the redirect.
      console.warn("Token requires interaction, redirecting to login");
      await clearAuthState();
      if (onAuthError) {
        void Promise.resolve(onAuthError());
      }
      return null;
    }

    console.error("Token acquisition error:", error);
    return null;
  }
};

/**
 * Authenticated fetcher utility
 * Automatically includes auth token from MSAL (with auto-refresh)
 * Handles token expiration and 401 errors
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

export const fetcher = async <T = unknown>({
  url,
  method = "GET",
  body,
  noContentType = false,
}: FetcherOptions): Promise<T> => {
  const token = await getValidToken();

  if (!token) {
    throw createFetcherError("No auth token found - please login", {
      status: 401,
    });
  }

  const headers = new Headers();
  headers.append("Authorization", `Bearer ${token}`);

  if (!noContentType) {
    headers.append("Content-Type", "application/json");
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${url}`, {
      method,
      headers,
      body,
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : "Network request failed";
    throw createFetcherError(message, { isNetworkError: true });
  }

  if (response.status === 401 || response.status === 403) {
    const newToken = await getValidToken();

    if (!newToken) {
      await clearAuthState();
      if (onAuthError) {
        void Promise.resolve(onAuthError());
      }
      throw createFetcherError("Authentication failed - please login", {
        status: 401,
      });
    }

    headers.set("Authorization", `Bearer ${newToken}`);
    let retryResponse: Response;
    try {
      retryResponse = await fetch(`${API_BASE}${url}`, {
        method,
        headers,
        body,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Network request failed";
      throw createFetcherError(message, { isNetworkError: true });
    }

    if (!retryResponse.ok) {
      if (retryResponse.status === 401 || retryResponse.status === 403) {
        console.error("Authentication failed even after token refresh");
        await clearAuthState();
        if (onAuthError) {
          void Promise.resolve(onAuthError());
        }
      }
      const errorText = await retryResponse.text();
      throw createFetcherError(`Error ${retryResponse.status}: ${errorText}`, {
        status: retryResponse.status,
        body: errorText,
      });
    }

    return parseResponse<T>(retryResponse);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw createFetcherError(`Error ${response.status}: ${errorText}`, {
      status: response.status,
      body: errorText,
    });
  }

  return parseResponse<T>(response);
};

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("Content-Type");

  if (contentType?.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  if (contentType?.includes("text/calendar")) {
    return response.text() as Promise<T>;
  }

  if (contentType?.includes("text/plain")) {
    return response.text() as Promise<T>;
  }

  if (contentType?.includes("application/octet-stream")) {
    return response.blob() as Promise<T>;
  }

  if (contentType?.includes("application/zip")) {
    return response.blob() as Promise<T>;
  }

  return response.text() as Promise<T>;
}
